/**
 * Folha A4 de apontamento: medidas em px CSS a 96 dpi (paisagem 1123 × 794,
 * retrato 794 × 1123), como na especificação aprovada na fábrica. A folha é
 * PAPEL: cores e tipografia próprias, fixas, iguais no tema claro e escuro.
 * O mesmo componente desenha a prévia (escalada), a impressão e o PDF.
 */
import React from "react";
import styled, { css } from "styled-components";

import { FONTE_CORPO, FONTE_TITULO } from "./fontes";

export const PAPEL = Object.freeze({
  tinta: "#1C1B1A",
  secundario: "#4A4644",
  rotulo: "#6F6A66",
  naoSeAplica: "#8A8580",
  bordaExterna: "#D9D6D2",
  bordaInterna: "#E6E2DE",
  vermelho: "#E21E3C",
  descricao: "#2E2A28",
  entregaFundo: "#FCE9EC",
  entregaTexto: "#9E1029",
  alertaTela: "#E21E3C",
});

export const LARGURA_PAISAGEM = 1123;
export const ALTURA_PAISAGEM = 794;

const exato = css`
  print-color-adjust: exact;
  -webkit-print-color-adjust: exact;
`;

export const Papel = styled.article`
  ${exato};
  position: relative;
  box-sizing: border-box;
  width: ${({ $retrato }) => ($retrato ? ALTURA_PAISAGEM : LARGURA_PAISAGEM)}px;
  height: ${({ $retrato }) => ($retrato ? LARGURA_PAISAGEM : ALTURA_PAISAGEM)}px;
  padding: ${({ $retrato }) => ($retrato ? "40px" : "40px 44px")};
  display: flex;
  flex-direction: column;
  gap: ${({ $retrato }) => ($retrato ? 10 : 14)}px;
  overflow: hidden;
  background: #fff;
  color: ${PAPEL.tinta};
  font-family: ${FONTE_CORPO};
  font-size: 12.5px;
  line-height: 1.25;
  text-align: left;
  letter-spacing: normal;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

// ------------------------------------------------------------ cabeçalho

export const Cabecalho = styled.header`
  flex: 0 0 auto;
  min-height: 60px;
  display: flex;
  align-items: center;
  gap: 20px;
`;

export const Logo = styled.div`
  flex: 0 0 auto;
  height: 40px;
  max-width: 170px;
  display: flex;
  align-items: center;

  img {
    display: block;
    height: 40px;
    width: auto;
    max-width: 170px;
    object-fit: contain;
  }

  span {
    font-family: ${FONTE_TITULO};
    font-size: 20px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    line-height: 1;
  }
`;

export const Divisor = styled.span`
  flex: 0 0 1px;
  align-self: stretch;
  background: ${PAPEL.bordaExterna};
`;

export const BlocoTitulo = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  max-width: 300px;
`;

export const Sobretitulo = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ $cor }) => $cor};
  margin-bottom: 4px;
`;

export const Titulo = styled.h1`
  margin: 0;
  font-family: ${FONTE_TITULO};
  font-size: ${({ $tamanho }) => $tamanho || 34}px;
  font-weight: 800;
  line-height: 0.95;
  text-transform: uppercase;
  color: ${PAPEL.tinta};
`;

export const Trilha = styled.ol`
  flex: 1 1 auto;
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: 6px;
  font-family: ${FONTE_TITULO};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${PAPEL.rotulo};
  white-space: nowrap;

  li + li::before {
    content: "›";
    margin-right: 6px;
  }

  .atual {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 999px;
    background: ${({ $cor }) => $cor};
    color: #fff;
  }
`;

export const SeloLote = styled.div`
  ${exato};
  flex: 0 0 96px;
  height: 60px;
  padding: 4px;
  border-radius: 8px;
  background: ${({ $cor }) => $cor};
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;

  .rotulo {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
  }

  .numero {
    font-family: ${FONTE_TITULO};
    font-size: ${({ $numeroLongo }) => ($numeroLongo ? 20 : 34)}px;
    font-weight: 800;
    line-height: 1;
    max-width: 88px;
    overflow-wrap: anywhere;
  }

`;

// ------------------------------------------------- faixa de informações

export const Faixa = styled.section`
  flex: 0 0 88px;
  display: flex;
  gap: 14px;
  min-height: 88px;
`;

export const Painel = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border: 1px solid ${PAPEL.bordaExterna};
  border-radius: 8px;
  overflow: hidden;
`;

export const Celula = styled.div`
  min-width: 0;
  padding: 10px 18px 3px;

  & + & {
    border-left: 1px solid ${PAPEL.bordaInterna};
  }
`;

export const Rotulo = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ $claro }) => ($claro ? "rgba(255,255,255,0.9)" : PAPEL.rotulo)};
  line-height: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

export const Valor = styled.div`
  margin-top: 5px;
  font-family: ${FONTE_TITULO};
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  sup {
    font-size: 0.6em;
    line-height: 0;
  }
`;

export const Detalhe = styled.div`
  margin-top: 3px;
  font-size: 12px;
  color: ${PAPEL.secundario};
  line-height: 1.1;
`;

export const Cartao = styled.div`
  ${exato};
  flex: 0 0 228px;
  border-radius: 8px;
  padding: 13px 20px 8px;
  background: ${({ $fundo }) => $fundo};
  color: #fff;

  .data {
    margin-top: 5px;
    font-family: ${FONTE_TITULO};
    font-size: 34px;
    font-weight: 800;
    line-height: 1;
  }

  .dia {
    margin-top: 2px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.2;
  }
`;

export const Ponto = styled.span`
  ${exato};
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${PAPEL.vermelho};
`;

// --------------------------------------------------------------- tabela

/**
 * Degraus do ajuste (utils/apontamentos.proximoAjuste), em variáveis de CSS
 * para valerem igual na prévia, na impressão e no PDF:
 *   1 — linhas mais baixas; 2 — letra menor; 3 — Observações menor (Papel).
 */
const DEGRAUS = [
  { linhaFixa: 32, linhaComTexto: 54, branca: 30, celula: "4px 5px", item: "7px 8px", fonte: null, descricao: 10, qtd: 14 },
  { linhaFixa: 28, linhaComTexto: 40, branca: 22, celula: "2px 5px", item: "4px 6px", fonte: null, descricao: 10, qtd: 14 },
  { linhaFixa: 24, linhaComTexto: 34, branca: 20, celula: "2px 4px", item: "3px 5px", fonte: 11, descricao: 9, qtd: 12 },
];

function degrau(nivel) {
  return DEGRAUS[Math.min(nivel || 0, DEGRAUS.length - 1)];
}

export const Tabela = styled.section`
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid ${PAPEL.bordaExterna};
  border-radius: 8px;
  overflow: hidden;
  /* $escala < 1 só depois do último degrau: letra e linhas na proporção que
     falta; as colunas ficam com a largura aprovada. */
  font-size: ${({ $compacta, $nivel, $escala = 1 }) => (degrau($nivel).fonte || ($compacta ? 12 : 12.5)) * $escala}px;
  --linha-fixa: ${({ $nivel, $escala = 1 }) => degrau($nivel).linhaFixa * $escala}px;
  --linha-com-texto: ${({ $nivel, $escala = 1 }) => degrau($nivel).linhaComTexto * $escala}px;
  --linha-branca: ${({ $nivel, $escala = 1 }) => degrau($nivel).branca * $escala}px;
  --padding-celula: ${({ $nivel }) => degrau($nivel).celula};
  --padding-item: ${({ $nivel }) => degrau($nivel).item};
  --fonte-descricao: ${({ $nivel, $escala = 1 }) => degrau($nivel).descricao * $escala}px;
  --fonte-quantidade: ${({ $nivel, $escala = 1 }) => degrau($nivel).qtd * $escala}px;
`;

const grade = css`
  display: grid;
  grid-template-columns: ${({ $grade }) => $grade};
`;

export const LinhaCabecalho = styled.div`
  ${exato};
  ${grade};
  flex: 0 0 40px;
  min-height: 40px;
  background: ${({ $cor }) => $cor};
  color: #fff;
  font-family: ${FONTE_TITULO};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1.1;

  > div {
    ${exato};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 0 5px;
    text-align: center;
    min-width: 0;
  }

  > div:first-child {
    justify-content: flex-start;
    padding-left: 12px;
    text-align: left;
  }

  > .limite {
    background: ${({ $deep }) => $deep};
  }

  > .entrega {
    background: ${PAPEL.tinta};
  }
`;

export const Linha = styled.div`
  ${grade};
  border-top: 1px solid ${PAPEL.bordaInterna};
  ${({ $variavel }) => ($variavel
    ? css`
        flex: 0 0 auto;
        min-height: var(--linha-com-texto, 54px);
      `
    : css`
        flex: 1 1 0;
        min-height: var(--linha-fixa, 32px);
      `)}

  ${({ $branca, $variavel }) => $branca && $variavel && css`
    flex: 1 1 0;
    min-height: var(--linha-branca, 30px);
  `}

  > div {
    ${exato};
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--padding-celula, 4px 5px);
    text-align: center;
  }

  > div + div {
    border-left: 1px solid ${PAPEL.bordaInterna};
  }

  > .cliente {
    justify-content: flex-start;
    padding-left: 12px;
    text-align: left;
    font-weight: 500;
  }

  /* Reticências só na Marcenaria (especificação); nos outros setores o nome
     quebra em até duas linhas, porque o html2canvas do PDF corta a
     reticência no meio da letra. */
  > .cliente span {
    display: block;
    min-width: 0;
    overflow: hidden;
    line-height: 1.2;
    max-height: 3.6em;
    ${({ $compacta }) => ($compacta
    ? css`
        text-overflow: ellipsis;
        white-space: nowrap;
      `
    : css`
        white-space: normal;
        overflow-wrap: anywhere;
      `)}
  }

  > .projeto {
    font-weight: 700;
  }

  > .limite {
    background: ${({ $tint }) => $tint};
    color: ${({ $deep }) => $deep};
    flex-direction: column;
  }

  > .entrega {
    background: ${PAPEL.entregaFundo};
    color: ${PAPEL.entregaTexto};
    flex-direction: column;
  }

  > .item-descrito {
    align-items: flex-start;
    justify-content: flex-start;
    flex-direction: column;
    gap: 2px;
    padding: var(--padding-item, 7px 8px);
    text-align: left;
  }

  > .com-aviso {
    outline: 2px dashed ${PAPEL.alertaTela};
    outline-offset: -3px;
  }
`;

export const DataNaCelula = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.05;

  .data {
    font-family: ${({ $forte }) => ($forte ? FONTE_TITULO : "inherit")};
    font-size: ${({ $forte }) => ($forte ? 15 : 12.5)}px;
    font-weight: ${({ $forte }) => ($forte ? 800 : 400)};
  }

  .dia {
    font-size: 10px;
    font-weight: ${({ $forte }) => ($forte ? 600 : 400)};
    color: ${({ $forte }) => ($forte ? "inherit" : PAPEL.rotulo)};
  }
`;

export const NaoSeAplica = styled.span`
  color: ${PAPEL.naoSeAplica};
  font-size: 12px;
`;

export const Quantidade = styled.span`
  font-family: ${FONTE_TITULO};
  font-size: var(--fonte-quantidade, 14px);
  font-weight: 800;
  line-height: 1;
  color: ${({ $cor }) => $cor};
`;

export const Descricao = styled.span`
  display: block;
  font-size: var(--fonte-descricao, 10px);
  line-height: 1.25;
  text-transform: uppercase;
  color: ${PAPEL.descricao};
  overflow-wrap: anywhere;
  word-break: normal;
`;

// ------------------------------------------------- observações e rodapé

export const Observacoes = styled.section`
  flex: 0 0 ${({ $altura }) => $altura || 112}px;
  min-height: ${({ $altura }) => $altura || 112}px;
  border: 1px solid ${PAPEL.bordaExterna};
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  ${({ $flexivel }) => $flexivel && css`
    flex: 1 1 auto;
    min-height: 180px;
  `}

  /* Linhas reais, não repeating-linear-gradient: o html2canvas do PDF não
     desenha gradiente repetido e a pauta sumia do arquivo. */
  .pauta {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  .pauta span {
    display: block;
    height: 26px;
    border-bottom: 1px solid ${PAPEL.bordaInterna};
  }
`;

export const Rodape = styled.footer`
  flex: 0 0 24px;
  display: flex;
  align-items: flex-end;
  gap: 28px;
  font-size: 12.5px;
  color: ${PAPEL.secundario};
`;

export const CampoRodape = styled.div`
  flex: ${({ $largura }) => ($largura ? `0 0 ${$largura}px` : "1 1 auto")};
  display: flex;
  align-items: flex-end;
  gap: 10px;
  min-width: 0;
  white-space: nowrap;

  .linha {
    flex: 1 1 auto;
    border-bottom: 1px solid ${PAPEL.tinta};
    height: 1px;
  }
`;

const LINHAS_DE_PAUTA = 40;

/** Linhas pautadas a cada 26 px; as que não cabem ficam escondidas. */
export function Pauta() {
  return (
    <div className="pauta" aria-hidden="true">
      {Array.from({ length: LINHAS_DE_PAUTA }, (_, i) => <span key={i} />)}
    </div>
  );
}

export const MarcaDeVia = styled.div`
  position: absolute;
  right: 44px;
  bottom: 12px;
  font-size: 9.5px;
  color: ${PAPEL.naoSeAplica};
  letter-spacing: 0.02em;
`;
